import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Space, Spin, Switch, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import {
    getOutreachProspects,
    updateOutreachProspectDoNotSend,
    updateOutreachProspectMarketingBasis,
} from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB');
};

const Leads = () => {
    const pageLayoutProps = usePageLayout();
    const { role } = pageLayoutProps;
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [updatingProspectId, setUpdatingProspectId] = useState('');
    const [filters, setFilters] = useState({
        country: 'uk',
        contacted: '',
        search: '',
    });
    const [searchDraft, setSearchDraft] = useState('');

    const loadProspects = useCallback(async () => {
        setLoading(true);
        try {
            const result = await getOutreachProspects({
                limit: 1000,
                country: filters.country || undefined,
                contacted: filters.contacted === '' ? undefined : filters.contacted === 'true',
                search: filters.search || undefined,
            });
            setProspects(Array.isArray(result?.prospects) ? result.prospects : []);
        } catch (error) {
            enqueueSnackbar('Failed to load outreach prospects', { variant: 'error' });
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, filters.contacted, filters.country, filters.search]);

    useEffect(() => {
        if (role && !isSuperAdmin(role)) {
            window.location.href = '/';
            return;
        }
        if (!role) return;
        loadProspects();
    }, [loadProspects, role]);

    const handleDoNotSendChange = useCallback(async (record, doNotSend) => {
        if (!record?._id) return;
        setUpdatingProspectId(record._id);
        try {
            const result = await updateOutreachProspectDoNotSend({
                prospectId: record._id,
                doNotSend,
            });
            const updated = result?.prospect;
            setProspects((current) => current.map((item) => (
                item._id === record._id ? { ...item, ...updated, doNotSend } : item
            )));
            enqueueSnackbar(doNotSend ? 'Prospect blocked from contact' : 'Prospect unblocked for contact', { variant: 'success' });
        } catch (error) {
            const apiMessage = error?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to update contact block setting', { variant: 'error' });
            Sentry.captureException(error);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const handleMarketingBasisChange = useCallback(async (record, marketingBasis) => {
        if (!record?._id || !marketingBasis) return;
        setUpdatingProspectId(record._id);
        try {
            const result = await updateOutreachProspectMarketingBasis({
                prospectId: record._id,
                marketingBasis,
            });
            const updated = result?.prospect;
            setProspects((current) => current.map((item) => (
                item._id === record._id ? { ...item, ...updated, marketingBasis } : item
            )));
            enqueueSnackbar('Contact permission basis updated', { variant: 'success' });
        } catch (error) {
            const apiMessage = error?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to update contact permission basis', { variant: 'error' });
            Sentry.captureException(error);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const handleAllowContact = useCallback(async (record) => {
        if (!record?._id) return;
        setUpdatingProspectId(record._id);
        try {
            const [marketingResult, sendResult] = await Promise.all([
                updateOutreachProspectMarketingBasis({
                    prospectId: record._id,
                    marketingBasis: 'documented_legitimate_interest',
                }),
                updateOutreachProspectDoNotSend({
                    prospectId: record._id,
                    doNotSend: false,
                }),
            ]);
            setProspects((current) => current.map((item) => (
                item._id === record._id
                    ? {
                        ...item,
                        ...(marketingResult?.prospect || {}),
                        ...(sendResult?.prospect || {}),
                        marketingBasis: 'documented_legitimate_interest',
                        doNotSend: false,
                        canContact: true,
                    }
                    : item
            )));
            enqueueSnackbar('Prospect approved for contact', { variant: 'success' });
        } catch (error) {
            const apiMessage = error?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to approve prospect for contact', { variant: 'error' });
            Sentry.captureException(error);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const summary = useMemo(() => {
        const contacted = prospects.filter((prospect) => prospect.contacted).length;
        const notContacted = prospects.length - contacted;
        const allowed = prospects.filter((prospect) => prospect.canContact).length;
        return { contacted, notContacted, allowed };
    }, [prospects]);

    const columns = [
        {
            title: 'Company',
            dataIndex: 'companyName',
            width: 220,
            render: (value) => value || '—',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            width: 240,
            render: (value) => value || '—',
        },
        {
            title: 'Market',
            dataIndex: 'country',
            width: 90,
            render: (value) => (value || '—').toUpperCase(),
        },
        {
            title: 'Region',
            dataIndex: 'countyOrArea',
            width: 140,
            render: (value) => value || '—',
        },
        {
            title: 'Contacted',
            width: 140,
            render: (_, record) => record.contacted ? <Tag color="blue">Contacted</Tag> : <Tag>Not contacted</Tag>,
        },
        {
            title: 'Last contact activity',
            dataIndex: 'contactedAt',
            width: 180,
            render: formatDateTime,
        },
        {
            title: 'Last event',
            dataIndex: 'lastEmailEventType',
            width: 130,
            render: (value) => value ? <Tag>{value}</Tag> : '—',
        },
        {
            title: 'Engagement',
            width: 220,
            render: (_, record) => (
                <Space size={4}>
                    <Tag>Opens: {record.opens || 0}</Tag>
                    <Tag>Clicks: {record.clicks || 0}</Tag>
                    <Tag>Bounces: {record.bounced || 0}</Tag>
                </Space>
            ),
        },
        {
            title: 'Allowed to contact',
            dataIndex: 'canContact',
            width: 170,
            render: (value) => value ? <Tag color="green">Allowed</Tag> : <Tag color="red">Blocked</Tag>,
        },
        {
            title: 'Marketing basis',
            dataIndex: 'marketingBasis',
            width: 240,
            render: (value, record) => (
                <Select
                    value={value || 'unverified'}
                    loading={updatingProspectId === record._id}
                    onChange={(marketingBasis) => handleMarketingBasisChange(record, marketingBasis)}
                    style={{ width: '100%' }}
                    options={[
                        { value: 'unverified', label: 'Unverified - blocked' },
                        { value: 'documented_legitimate_interest', label: 'Legitimate prospect' },
                        { value: 'explicit_consent', label: 'Explicit consent' },
                    ]}
                />
            ),
        },
        {
            title: 'Do not send',
            dataIndex: 'doNotSend',
            width: 130,
            render: (value, record) => (
                <Switch
                    checked={value === true}
                    loading={updatingProspectId === record._id}
                    onChange={(checked) => handleDoNotSendChange(record, checked)}
                />
            ),
        },
        {
            title: 'Quick action',
            width: 190,
            render: (_, record) => (
                <Button
                    size="small"
                    type={record.canContact ? 'default' : 'primary'}
                    disabled={record.canContact}
                    loading={updatingProspectId === record._id}
                    onClick={() => handleAllowContact(record)}
                >
                    {record.canContact ? 'Contact allowed' : 'Allow contact'}
                </Button>
            ),
        },
    ];

    if (!role) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Spin />
            </PageLayout>
        );
    }

    if (!isSuperAdmin(role)) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Typography.Title level={4}>Outreach Contact Queue</Typography.Title>
                <Typography.Text type="secondary">Super admin access is required.</Typography.Text>
            </PageLayout>
        );
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1600, margin: '0 auto' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Typography.Title level={2} style={{ margin: 0 }}>Outreach Contact Queue</Typography.Title>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={loadProspects}>Refresh</Button>
                </Space>

                <Typography.Paragraph type="secondary">
                    This list tracks outreach prospects, whether they have already been contacted, and whether they are currently allowed to receive outreach emails.
                </Typography.Paragraph>

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} md={8}><Card><Typography.Text type="secondary">Contacted</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.contacted}</Typography.Title></Card></Col>
                    <Col xs={24} md={8}><Card><Typography.Text type="secondary">Not contacted</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.notContacted}</Typography.Title></Card></Col>
                    <Col xs={24} md={8}><Card><Typography.Text type="secondary">Allowed to contact</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.allowed}</Typography.Title></Card></Col>
                </Row>

                <Card style={{ marginBottom: 16 }}>
                    <Space wrap>
                        <Select
                            style={{ width: 150 }}
                            value={filters.country}
                            onChange={(value) => setFilters((current) => ({ ...current, country: value }))}
                            options={[
                                { value: '', label: 'All markets' },
                                { value: 'uk', label: 'UK' },
                                { value: 'us', label: 'US' },
                                { value: 'de', label: 'DE' },
                            ]}
                        />
                        <Select
                            style={{ width: 180 }}
                            value={filters.contacted}
                            onChange={(value) => setFilters((current) => ({ ...current, contacted: value }))}
                            options={[
                                { value: '', label: 'All contact states' },
                                { value: 'false', label: 'Not contacted' },
                                { value: 'true', label: 'Contacted' },
                            ]}
                        />
                        <Input
                            style={{ width: 280 }}
                            value={searchDraft}
                            placeholder="Search company, email, website"
                            onChange={(event) => setSearchDraft(event.target.value)}
                            onPressEnter={() => setFilters((current) => ({ ...current, search: searchDraft.trim() }))}
                            allowClear
                        />
                        <Button onClick={() => setFilters((current) => ({ ...current, search: searchDraft.trim() }))}>Apply</Button>
                    </Space>
                </Card>

                <Card>
                    {loading ? <Spin /> : (
                        <Table
                            rowKey={(record) => record._id}
                            dataSource={prospects}
                            columns={columns}
                            pagination={{ pageSize: 20 }}
                            scroll={{ x: 2200 }}
                            locale={{ emptyText: 'No outreach prospects match these filters.' }}
                        />
                    )}
                </Card>
            </div>
        </PageLayout>
    );
};

export default Leads;
